import { Component } from "@theme/component"
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities"
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from "@theme/events"
import { cartPerformance } from "@theme/performance"
import { morph } from "@theme/morph"
import { resolveBestMerchantVariant, readProductDataForId, readSelectedVisibleOptions } from "@theme/merchant-resolver"

function collectProperties(formEl) {
  const fd = new FormData(formEl)
  const props = {}
  for (const [key, val] of fd.entries()) {
    const m = /^properties\[(.+)\]$/.exec(key)
    if (!m) continue
    if (val instanceof File) continue
    if (val == null || val === "") continue
    const name = m[1]
    props[name] = props[name] ? `${props[name]}, ${val}` : String(val)
  }
  return props
}

function getVariantTitleForForm() {
  const formEl = document.querySelector("variant-custom-picker form")
  if (!formEl) return

  const selects = Array.from(formEl.querySelectorAll('select[name^="options"]'))

  const optionData = selects
    .map((sel) => {
      const selectedOption = sel.options[sel.selectedIndex]
      if (!selectedOption) return null

      const value = selectedOption.value?.trim()
      const variantId = selectedOption.dataset.variantId?.trim()

      if (!value) return null
      return { value, variantId }
    })
    .filter(Boolean)

  if (optionData.length) {
    const values = optionData.map((o) => o.value).join(" / ")
    const ids = optionData
      .map((o) => o.variantId)
      .filter(Boolean)
      .join("_")
    return `${values}${ids ? " #" + ids : ""}`
  }

  // neutral fallback
  return "Variante"
}

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"]

  /** @type {number | undefined} */
  #animationTimeout

  /** @type {number | undefined} */
  #cleanupTimeout

  connectedCallback() {
    super.connectedCallback()

    this.addEventListener("pointerenter", this.#preloadImage)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout)
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout)
    this.removeEventListener("pointerenter", this.#preloadImage)
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart()

    if (!event.target.closest(".quick-add-modal")) this.#animateFlyToCart()
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia

    if (!image) return

    preloadImage(image)
  }

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs
    const cartIcon = document.querySelector(".header-actions__cart-icon")

    const image = this.dataset.productVariantMedia

    if (!cartIcon || !addToCartButton || !image) return

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement("fly-to-cart"))

    flyToCartElement.style.setProperty("background-image", `url(${image})`)
    flyToCartElement.source = addToCartButton
    flyToCartElement.destination = cartIcon

    document.body.appendChild(flyToCartElement)
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout)
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout)

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added")
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove("atc-added")
      }, 10)
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION)
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent)
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class CustomProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"]
  #abortController = new AbortController()

  /** @type {number | undefined} */
  #timeout

  connectedCallback() {
    super.connectedCallback()

    const { signal } = this.#abortController
    const target = this.closest(".shopify-section, dialog, product-card, .product__options-combined")
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    })
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal })
    target?.addEventListener("variant:selected", this.#onSimpleVariantUpdate, { signal })

    // Products without real variant options (has_only_default_variant) never render
    // <variant-custom-picker> at all (see custom-variant-picker.liquid: default_variant_picker skips
    // the whole element) — none of the variant events above ever fire, so nothing would otherwise
    // gate the button on attached additional products / fabric / date; it would stay in its
    // server-rendered default (enabled) state forever. Take that job over directly in that case.
    // Where a variant picker exists, it already owns gating (including inventory) via its own
    // updateInventoryHint(), so stay out of its way there.
    if (!target?.querySelector("variant-custom-picker")) {
      this.addEventListener("change", this.#applySelectionGating, { signal })
      setTimeout(this.#applySelectionGating, 0)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.#abortController.abort()
  }

  /**
   * Fallback gate for products without a <variant-custom-picker> (see connectedCallback). Purely
   * about attached-selection requirements — doesn't touch inventory/availability, since there's no
   * dynamic variant to track in that case (the ATC row is already server-side gated on stock, see
   * product-options-combined.liquid).
   */
  #applySelectionGating = () => {
    const container = this.refs.addToCartButtonContainer
    if (!container) return
    if (this.#hasUnmetSelectionRequirement()) {
      container.disable()
    } else {
      container.enable()
    }
  }

  /**
   * Whether any required selection this product carries is still missing: additional products
   * (product-mix / configuration_products, rendered as select[data-upgrade-product] — see
   * product-mix-products.liquid / product-configuration-products.liquid), fabric pickers
   * (product-fabric-color-selector.liquid), or the refurbishment date
   * (product-refurbishment-date.liquid). Mirrors the canAtc check in variant-custom-picker.js's
   * updateInventoryHint() exactly, so this component's own enable()/disable() calls (triggered by
   * variant-update events, which fire on every initialization — see syncVariantFromSelection() in
   * variant-custom-picker.js — not just genuine user interaction) can never diverge from it and
   * re-enable the button while something is still unselected. Products that don't carry a given
   * requirement simply have no matching elements, so that part is always satisfied for them.
   * @returns {boolean}
   */
  #hasUnmetSelectionRequirement() {
    const hasUnselectedUpgradeProduct = /** @type {HTMLSelectElement[]} */ (
      Array.from(this.querySelectorAll("select[data-upgrade-product]"))
    ).some((select) => !select.disabled && (select.value ?? "").trim() === "")
    if (hasUnselectedUpgradeProduct) return true

    const hasInvalidFabricPicker = Array.from(this.querySelectorAll("variant-fabric-picker")).some((picker) => {
      if (picker.hasAttribute("data-optional")) return false
      const input = picker.querySelector("[data-accessory-variant-id]")
      if (!input || !(input instanceof HTMLInputElement)) return true
      const value = input.value.trim()
      const zeroish = /^(?:0|€?\s*0(?:[.,]00)?)$/
      return !value || value === "null" || value === "undefined" || zeroish.test(value)
    })
    if (hasInvalidFabricPicker) return true

    const dateInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.refurbishment-date input[type="date"][required]'))
    if (dateInput && (!dateInput.value || dateInput.classList.contains("refurbishment-date--invalid"))) return true

    return false
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs
    // Stop default behaviour from the browser
    event.preventDefault()

    if (this.#timeout) clearTimeout(this.#timeout)

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.disabled) return
    // optionally more robust:
    const btn = this.refs.addToCartButtonContainer?.refs.addToCartButton
    if (!btn || btn.disabled || btn.hasAttribute("disabled")) return

    // Hard stop, independent of the button's disabled state: every attached additional product
    // must have a selection before the item can go into the cart.
    if (this.#hasUnmetSelectionRequirement()) return

    // Send the add to cart information to the cart
    const form = this.querySelector("form")

    if (!form) throw new Error("Product form element missing")

    const formData = new FormData(form)
    const lineItemProps = collectProperties(form)

    /////// START Logic for upgrade products and fabrics

    // select main product
    const mainProductId = this.dataset.productId
    const mainVariantId = form.querySelector('input[name="id"], select[name="id"]')?.value
    const mainQuantity = parseInt(form.querySelector('input[name="quantity"]')?.value || "1", 10)
    if (!mainVariantId) {
      console.error("No variant selection found.")
      return
    }

    // Multi-merchant smart selection: when the same visible-option combo exists
    // across several Merchant variants, route to the sibling with the highest
    // tracked stock (covering the requested qty if possible). Falls through to
    // the page-selected variant when the product is not merchant-split or no
    // sibling has positive stock — Stage 2 (order webhook) handles the rest.
    let effectiveMainVariantId = mainVariantId
    try {
      const productData = readProductDataForId(mainProductId)
      const selectedVisibleOptions = readSelectedVisibleOptions(mainProductId)
      const resolved = resolveBestMerchantVariant(productData, selectedVisibleOptions, mainQuantity)
      if (resolved != null) effectiveMainVariantId = String(resolved)
    } catch (err) {
      console.warn("[merchant-resolver] resolve failed, using page variant", err)
    }

    // --- Upgrade-/Accessory-IDs + Typ einsammeln (Radios = fabric, Selects = upgrade) ---
    // 1) Hidden inputs aus Fabric/Color-Section (product-fabric-color-selector.liquid).
    //    Wir lesen den variantInput direkt, nicht die Radios — der bleibt im Form
    //    auch wenn das Cascade-Panel ins <body> portaliert wurde.
    const fabricVariantInputs = Array.from(form.querySelectorAll("input[data-accessory-variant-id]"))
    const fabricEntries = fabricVariantInputs
      .map((el) => {
        const id = el.value
        if (!id) return null
        return { id, type: "fabric" }
      })
      .filter(Boolean)

    // 2) Dropdown-Upgrades (product-configuration-products.liquid, product-mix-products.liquid)
    const selectedUpgradeSelects = Array.from(form.querySelectorAll("select[data-upgrade-product]"))
    const upgradeEntries = selectedUpgradeSelects
      .map((sel) => {
        const id = sel.value
        if (!id) return null
        return { id, type: "upgrade", selectKey: sel.dataset.key || "" }
      })
      .filter(Boolean)

    // 3) Combine + deduplicate by id (in case of conflict, keep the first found type)
    const byId = new Map()
    ;[...fabricEntries, ...upgradeEntries].forEach((entry) => {
      if (!byId.has(entry.id)) byId.set(entry.id, entry)
    })
    const accessoryEntries = Array.from(byId.values())

    // sort additional products
    const sortedUpgradeIds = accessoryEntries.map((e) => e.id).sort()
    const isRefurbishment = this.dataset.productType && this.dataset.productType.trim() === "Refurbishment"
    const groupId = `${effectiveMainVariantId}__${sortedUpgradeIds.join("_")}${isRefurbishment ? "__" + Date.now() : ""}`

    // Resolved product-mix gallery image (see product-mix-gallery.js), snapshotted onto the main
    // item so cart/order reflect what the customer actually configured, not just the plain
    // default product image — and so it survives later edits to the underlying combo-image data.
    const optionsScope = this.closest(".product__options-combined")
    const configuredImage = optionsScope?.dataset.configuredImage

    // main item with quantity and lineitem properties

    const mainItem = {
      id: effectiveMainVariantId,
      quantity: mainQuantity,
      properties: {
        ...lineItemProps,
        _accessoryGroupId: groupId,
        ...(configuredImage ? { _configuredImage: configuredImage } : {}),
      },
    }

    // Accessory-/Upgrade-Items inkl. Typ-Flag (_accessoryType: 'fabric'|'upgrade')
    const upgradeItems = accessoryEntries
      .sort((a, b) => a.id.localeCompare(b.id))
      .map(({ id, type, selectKey }) => ({
        id,
        quantity: mainQuantity,
        properties: {
          // ...lineItemProps,
          _accessoryGroupId: groupId,
          _parentProduct: mainProductId,
          _isAccessory: "true",
          _accessoryType: type,
          ...(selectKey ? { _upgradeSelectKey: selectKey } : {}),
        },
      }))

    const allItems = [...upgradeItems, mainItem]

    // Check for file uploads (Refurbishment photos)
    const fileInputs = /** @type {HTMLInputElement[]} */ (Array.from(form.querySelectorAll('input[type="file"]')))
    const hasFiles = fileInputs.some((input) => input.files && input.files.length > 0)

    const cartItemsComponents = document.querySelectorAll("cart-items-component, custom-cart-items-component")
    let cartItemComponentsSectionIds = []
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId)
      }
    })

    let submitPromise

    if (hasFiles) {
      // Two-Step: FormData for main item (supports file uploads), then JSON for accessories
      const mainFormData = new FormData()
      mainFormData.append("id", effectiveMainVariantId)
      mainFormData.append("quantity", String(mainQuantity))
      for (const [key, val] of Object.entries(lineItemProps)) {
        mainFormData.append(`properties[${key}]`, val)
      }
      mainFormData.append("properties[_accessoryGroupId]", groupId)
      fileInputs.forEach((input) => {
        if (input.files && input.files.length > 0 && input.name) {
          mainFormData.append(input.name, /** @type {File} */ (input.files[0]))
        }
      })
      mainFormData.append("sections", cartItemComponentsSectionIds.join(","))

      submitPromise = fetch(Theme.routes.cart_add_url, {
        method: "POST",
        body: mainFormData,
      })
        .then((r) => r.json())
        .then((mainResponse) => {
          if (mainResponse.status) return mainResponse
          if (upgradeItems.length > 0) {
            const accCfg = fetchConfig("javascript", {
              body: JSON.stringify({
                items: upgradeItems,
                sections: cartItemComponentsSectionIds.join(","),
              }),
            })
            return fetch(Theme.routes.cart_add_url, {
              ...accCfg,
              headers: { ...accCfg.headers, Accept: "application/json", "Content-Type": "application/json" },
            })
              .then((r) => r.json())
              .then((accResponse) => (accResponse.status ? mainResponse : { ...mainResponse, sections: accResponse.sections || mainResponse.sections }))
          }
          return mainResponse
        })
    } else {
      const fetchCfg = fetchConfig("javascript", {
        body: JSON.stringify({
          items: allItems,
          sections: cartItemComponentsSectionIds.join(","),
        }),
      })
      submitPromise = fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      }).then((r) => r.json())
    }

    submitPromise
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message))

          if (!addToCartTextError) return
          addToCartTextError.classList.remove("hidden")

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2]
          if (textNode) {
            textNode.textContent = response.message
          } else {
            const newTextNode = document.createTextNode(response.message)
            addToCartTextError.appendChild(newTextNode)
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message)

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return
            addToCartTextError.classList.add("hidden")

            // Clear the announcement
            this.#clearLiveRegionText()
          }, 10000)

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount: Number(formData.get("quantity")) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          )

          return
        } else {
          const id = formData.get("id")

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden")
            addToCartTextError.removeAttribute("aria-live")
          }

          if (!id) throw new Error("Form ID is required")

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton
            const addedTextElement = addToCartButton.querySelector(".add-to-cart-text--added")
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added

            this.#setLiveRegionText(addedText)

            setTimeout(() => {
              this.#clearLiveRegionText()
            }, 5000)
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: "product-form-component",
              itemCount: Number(formData.get("quantity")) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            }),
          )

          window.dispatchEvent(new CustomEvent("cart:add"))
        }
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent("add:user-action", event)
      })
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion
    liveRegion.textContent = text
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion
    liveRegion.textContent = ""
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    // Check if HTML is available
    if (!event.detail?.data?.html) {
      return
    }

    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return
    }

    const scope =
      this.closest(".product-information") || // your shared wrapper
      this.closest(`[data-section-id="${this.dataset.sectionId}"]`) ||
      this // Fallback

    const { variantId, addToCartButtonContainer } = this.refs
    const sectionId = this.dataset.sectionId

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]')

    if (!currentAddToCartButton) return

    // Update the button state. Variant availability alone isn't enough — an attached additional
    // product (product-mix / configuration_products) may still be missing its required selection.
    if (event.detail.resource == null || event.detail.resource.available == false || this.#hasUnmetSelectionRequirement()) {
      addToCartButtonContainer.disable()
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute("hidden", "true")
    } else {
      addToCartButtonContainer.enable()
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden")
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton)
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? ""
    // --- Inventory tracking specific handling ---
    try {
      const sectionId = this.dataset.sectionId
      const scope = this.closest(".product-information") || this.closest(`[data-section-id="${sectionId}"]`) || this

      const qtyInput = scope.querySelector('quantity-selector-component [ref="quantityInput"]') || scope.querySelector('[ref="quantityInput"]')

      // Try to detect "tracked" from server-rendered HTML (preferred)
      const htmlScope = event.detail.data.html.querySelector(`[data-section-id="${sectionId}"]`) || event.detail.data.html

      const trackedFlagEl = htmlScope.querySelector("[data-inventory-tracked]")
      const isTracked = trackedFlagEl ? trackedFlagEl.dataset.inventoryTracked !== "false" : true

      if (qtyInput) {
        if (!isTracked) {
          // Untracked inventory: remove max, set flag to ignore max, and hide inventory hints
          qtyInput.removeAttribute("max")
          qtyInput.dataset.ignoreMax = "true"
          scope.querySelectorAll("[data-inventory-hint]").forEach((el) => el.setAttribute("hidden", "true"))
        } else {
          // Tracked inventory: restore ignore flag and sync max from new HTML if present
          delete qtyInput.dataset.ignoreMax
          const newQty = htmlScope.querySelector('quantity-selector-component [ref="quantityInput"]') || htmlScope.querySelector('[ref="quantityInput"]')
          if (newQty && newQty.max) qtyInput.max = newQty.max
          // Keep the hint hidden while product-mix selections are open (see updateInventoryHint()
          // in variant-custom-picker.js, which owns that state).
          const mixIncomplete = Array.from(scope.querySelectorAll("select[data-mix-cap]")).some((select) => !select.disabled && !(select.value ?? "").trim())
          if (!mixIncomplete) scope.querySelectorAll("[data-inventory-hint]").forEach((el) => el.removeAttribute("hidden"))
        }
      }
    } catch (e) {
      console.warn("Inventory tracking hook failed:", e)
    }
    // --- end inventory tracking handling ---

    // Set the data attribute for the add to cart button to the product variant media if it exists

    const currentGallery = scope.querySelector("[data-media-gallery]") || scope.querySelector(".product-media, [data-product-gallery]") // Fallbacks in case you have other hooks

    // Find the matching gallery for the same section in the server response (strict first, then loose)
    const newGallery = event.detail.data.html.querySelector(`[data-section-id="${sectionId}"] [data-media-gallery]`) || event.detail.data.html.querySelector("[data-media-gallery]") || event.detail.data.html.querySelector(".product-media, [data-product-gallery]")

    if (currentGallery && newGallery) {
      morph(currentGallery, newGallery)
    }

    // 6) Optional: Update ATC icon image
    if (event.detail.resource) {
      const src = event.detail.resource.featured_media?.preview_image?.src
      if (src) addToCartButtonContainer?.setAttribute("data-product-variant-media", `${src}&width=100`)
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable()
  }

  /**
   * Handles simple variant updates without HTML morphing (e.g. from force updates)
   * @param {CustomEvent} event - The variant:selected event
   */
  #onSimpleVariantUpdate = (event) => {
    const { variantId, addToCartButtonContainer } = this.refs

    if (!event.detail?.resource) {
      return
    }

    // Update variant id
    variantId.value = event.detail.resource.id ?? ""

    // Update Button State. This fires on every initialization (see syncVariantFromSelection() in
    // variant-custom-picker.js), not just genuine user interaction, so variant availability alone
    // must not re-enable the button while an attached additional product is still unselected.
    if (event.detail.resource.available === false || this.#hasUnmetSelectionRequirement()) {
      addToCartButtonContainer?.disable()
    } else {
      addToCartButtonContainer?.enable()
    }

    // Update ATC Button Media (for Fly-to-Cart Animation)
    if (event.detail.resource.featured_media?.preview_image?.src) {
      const src = event.detail.resource.featured_media.preview_image.src
      addToCartButtonContainer?.setAttribute("data-product-variant-media", `${src}&width=100`)
    }
  }
}

if (!customElements.get("custom-product-form-component")) {
  customElements.define("custom-product-form-component", CustomProductFormComponent)
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source

  /** @type {Element} */
  destination

  connectedCallback() {
    this.#animate()
  }

  #animate() {
    const rect = this.getBoundingClientRect()
    const sourceRect = this.source.getBoundingClientRect()
    const destinationRect = this.destination.getBoundingClientRect()

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    }
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    }

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    }

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 } // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 } // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null
    const duration = 600 // 600ms

    this.style.opacity = "1"

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint)

      //Update the position of the flying thingy
      this.style.setProperty("--x", `${position.x}px`)
      this.style.setProperty("--y", `${position.y}px`)

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5
      this.style.setProperty("--scale", `${scale}`)

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        //Fade out the flying thingy
        this.style.opacity = "0"
        onAnimationEnd(this, () => this.remove())
      }
    }

    // Position the flying thingy back to the start point
    this.style.setProperty("--x", `${startPoint.x}px`)
    this.style.setProperty("--y", `${startPoint.y}px`)

    //Start the animation
    requestAnimationFrame(animate)
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x)
  const bX = 3 * (p2.x - p1.x) - cX
  const aX = p3.x - p0.x - cX - bX

  const cY = 3 * (p1.y - p0.y)
  const bY = 3 * (p2.y - p1.y) - cY
  const aY = p3.y - p0.y - cY - bY

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y

  return { x, y }
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart)
}
